/**
 * Dispatch Contract — the scoped, machine-checkable brief a coding agent
 * carries into a governed dispatch.
 *
 * A DispatchContract is to a dev dispatch what an OutcomeContract
 * (src/lib/agent/outcomeContract.ts) is to a CRM proposal: the sealed
 * BEFORE-claim. It binds the ticket's goal to an explicit BlastScope —
 * which repos/paths the agent may touch, how many files, how many diff
 * lines — so the returned patch can be mechanically rejected the moment
 * it steps outside the blast radius. Enforcement happens BEFORE any
 * actionDiff is produced (see dispatch.ts), mirroring how
 * policyBlastRadius gates autonomy raises before Save.
 *
 * Deliberately pure: plain data in, plain data out. No store, no worker,
 * no clocks.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The blast radius a dispatched coding agent must stay inside. */
export type BlastScope = {
  /** Repos the agent may touch (e.g. ['argo-os']). */
  repos: string[];
  /** Path prefixes the agent may write under (e.g. ['src/lib/dev']). */
  paths: string[];
  /** Maximum number of files the patch may touch. */
  maxFiles: number;
  /** Maximum total changed lines (added + removed) across the patch. */
  maxDiffLines: number;
};

/** The sealed brief one dispatch carries: goal + scope + proof obligations. */
export type DispatchContract = {
  ticketId: string;
  goal: string;
  scope: BlastScope;
  /** Test commands that must pass before the patch is mergeable. */
  testsMustPass: string[];
};

/** One whole-file patch: the exact before and after content. */
export type FilePatch = {
  path: string;
  before: string;
  after: string;
};

/** Minimal ticket shape the builder needs — any dev ticket satisfies it. */
export type ContractTicketInput = {
  id: string;
  title: string;
  /** Optional richer goal statement; falls back to the title. */
  goal?: string;
  /** Optional explicit test obligations; falls back to the default suite. */
  tests?: string[];
};

/** The default proof obligation every dispatch carries when none is given. */
export const DEFAULT_TESTS: ReadonlyArray<string> = ['npx vitest run'];

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Compile a ticket + blast scope into a DispatchContract. Pure and
 * deterministic: same inputs → same contract. The scope is copied so the
 * sealed contract cannot drift if the caller later mutates its sliders.
 */
export function buildContract(
  ticket: ContractTicketInput,
  scope: BlastScope,
): DispatchContract {
  return {
    ticketId: ticket.id,
    goal: ticket.goal ?? ticket.title,
    scope: {
      repos: [...scope.repos],
      paths: [...scope.paths],
      maxFiles: scope.maxFiles,
      maxDiffLines: scope.maxDiffLines,
    },
    testsMustPass:
      ticket.tests && ticket.tests.length > 0 ? [...ticket.tests] : [...DEFAULT_TESTS],
  };
}

// ---------------------------------------------------------------------------
// Diff accounting
// ---------------------------------------------------------------------------

function splitLines(s: string): string[] {
  return s === '' ? [] : s.split('\n');
}

/**
 * Changed-line count for one file: added + removed, after trimming the
 * common prefix/suffix. A pure line-level measure — no external diff lib,
 * deterministic, and conservative (never undercounts a rewrite).
 */
export function changedLineCount(patch: FilePatch): number {
  const a = splitLines(patch.before);
  const b = splitLines(patch.after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  return endA - start + (endB - start);
}

/** True when `path` sits under one of the allowed prefixes. */
function underAllowedPath(path: string, prefixes: string[]): boolean {
  return prefixes.some(
    (p) => path === p || path.startsWith(p.endsWith('/') ? p : `${p}/`),
  );
}

// ---------------------------------------------------------------------------
// Enforcement
// ---------------------------------------------------------------------------

/**
 * Every way the patch escapes its blast radius — all violations, not just
 * the first, so the review card can show exactly why the dispatch was
 * rejected. An empty array means the patch is in scope.
 */
export function scopeViolations(patch: FilePatch[], scope: BlastScope): string[] {
  const violations: string[] = [];

  for (const file of patch) {
    if (!underAllowedPath(file.path, scope.paths)) {
      violations.push(
        `file outside allowed paths: ${file.path} (allowed: ${scope.paths.join(', ') || 'none'})`,
      );
    }
  }

  if (patch.length > scope.maxFiles) {
    violations.push(`too many files: ${patch.length} > maxFiles ${scope.maxFiles}`);
  }

  const totalChanged = patch.reduce((sum, f) => sum + changedLineCount(f), 0);
  if (totalChanged > scope.maxDiffLines) {
    violations.push(
      `diff too large: ${totalChanged} changed lines > maxDiffLines ${scope.maxDiffLines}`,
    );
  }

  return violations;
}
